import { LineItem } from "@made-people/grease-cart";
import { ProductWithVariants } from "@made-people/grease-product";
import { useTranslate } from "@made-people/grease-translations";
import { useCountriesAndLanguages } from "@made-people/grease-countries-and-languages";
import { getStockStatus } from "./out-of-stock-information";
import { getVariantStockDeliveryData } from "./get-stock-delivery-data";
import { useAllocationRule } from "../../../services/centra/allocation-rule/mm-allocation-rule-provider";
import { CartItemDeliveryStatus } from "./cart-item-delivery-status";
import { mmProductIsFixedBundle } from "../../../services/centra/mappers/mm-product-extra-attributes";

export const LineItemDeliveryStatus = ({
  lineItem,
}: {
  lineItem: LineItem;
}) => {
  const translate = useTranslate();
  const { currentCountryCode } = useCountriesAndLanguages();
  const {
    allocationRule,
    isLoading: isAllocationRuleLoading,
    hasWarehouse,
  } = useAllocationRule();
  const allocationRuleInfo = {
    name: allocationRule?.name ?? null,
    hasLitWarehouse: hasWarehouse("LIT"),
  };

  if (isAllocationRuleLoading) {
    return null;
  }

  if (lineItem.type !== "product") {
    return null;
  }

  const product = lineItem.product;
  const productBase = product.base;

  // Handle non-variant products (bundles, gift cards, etc.)
  if (product.dataModel !== "with_variants") {
    return (
      <CartItemDeliveryStatus
        status={translate("Delivery_In_Stock")}
        isImmediateDelivery={true}
      />
    );
  }

  if (mmProductIsFixedBundle(productBase)) {
    /**
     * If the product is fixed bundle, we know that the product is in stock.
     */
    return (
      <CartItemDeliveryStatus
        status={translate("Delivery_In_Stock")}
        isImmediateDelivery={true}
      />
    );
  }

  const productWithVariants = product as ProductWithVariants;
  const variant = productWithVariants.variants.find(
    (v) => v.variantId === lineItem.id,
  );

  if (!variant) {
    console.warn(
      `[LineItemDeliveryStatus] Variant not found for lineItem.id=${lineItem.id}, product=${productBase.productId}`,
    );
    return (
      <CartItemDeliveryStatus
        status={translate("Delivery_In_Stock")}
        isImmediateDelivery={true}
      />
    );
  }

  const variantStockData = getVariantStockDeliveryData(
    productBase,
    currentCountryCode,
  );

  // No stock data available - show in stock (product is in cart, so was purchasable)
  if (!variantStockData) {
    return (
      <CartItemDeliveryStatus
        status={translate("Delivery_In_Stock")}
        isImmediateDelivery={true}
      />
    );
  }

  // Use variant.label to match PDP (WithVariantsProductDeliveryStatus)
  const { deliveryStatus, isImmediateDelivery } = getStockStatus(
    variantStockData,
    variant.label,
    translate,
    allocationRuleInfo,
  );

  return (
    <CartItemDeliveryStatus
      status={deliveryStatus}
      isImmediateDelivery={isImmediateDelivery}
    />
  );
};
